import { Badge } from "@bcc/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@bcc/components/ui/dialog";
import { Separator } from "@bcc/components/ui/separator";
import { useBatchDetails } from "@bcc/hooks/use-batch-details";
import { useCreateShippingDocuments } from "@bcc/hooks/use-create-shipping-documents";
import { getRetryDisabledReason } from "@bcc/components/batches/batch-summary.utils";
import { getPurchaseOrderTarget } from "@client-shared/lib/links";
import { readableBatchId } from "@client-shared/lib/util";
import { useInstallationsState } from "@client-shared/providers/installations";
import { getMarketplaceDisplayName } from "../utils/get-marketplace-display-name";
import { CELL_LINK_CLASSES } from "../constants";
import { useConfigState } from "@client-shared/providers/config";
import { useVendorContext } from "@client-shared/providers/vendor";
import type { PurchaseOrder, ShippingDocumentsTabType } from "@shared/types";
import { MapPin, Package } from "lucide-react";
import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CarrierIcon } from "../carrier/carrier-icon";
import { ORDER_STATUS_CONFIG } from "../constants";
import { ORDER_DETAIL_SECTIONS, setOrderDetailDialogTestId } from "./order-detail-dialog.utils";
import { OrderDetailNotice } from "./order-detail-notice";
import { OrderDetailShippingButtons } from "./order-detail-shipping-buttons";
const ShippingDocumentsDialog = lazy(() =>
  import("../shipping-documents-dialog/shipping-documents-dialog").then((mod) => ({ default: mod.ShippingDocumentsDialog }))
);

interface OrderDetailDialogProps {
  purchaseOrder: PurchaseOrder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBatchClick: (batchId: string) => void;
  testId?: string;
}

export function OrderDetailDialog({ purchaseOrder, open, onOpenChange, onBatchClick, testId }: OrderDetailDialogProps) {
  const { t } = useTranslation();
  const [, , configUtils] = useConfigState();
  const [{ customerAccountCarrierDetails }] = useVendorContext();
  const { batch } = useBatchDetails(purchaseOrder?.batchId);
  const { isCreating, handleCreate } = useCreateShippingDocuments();

  const [shippingDocsOpen, setShippingDocsOpen] = useState(false);
  const [shippingDocsInitialTab, setShippingDocsInitialTab] = useState<ShippingDocumentsTabType>("combined");

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset shipping documents panel when order dialog closes
    if (!open) setShippingDocsOpen(false);
  }, [open]);

  const handleOpenShippingDocuments = useCallback((tab: ShippingDocumentsTabType) => {
    setShippingDocsInitialTab(tab);
    setShippingDocsOpen(true);
  }, []);

  const handleRetry = useCallback(() => {
    if (purchaseOrder) void handleCreate(purchaseOrder);
  }, [handleCreate, purchaseOrder]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- manual memoization preserved until React Compiler is enabled across the workspace
  const handleBatchLinkClick = useCallback(() => {
    if (purchaseOrder?.batchId) onBatchClick(purchaseOrder.batchId);
  }, [onBatchClick, purchaseOrder?.batchId]);

  const [{ installations }] = useInstallationsState();

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- manual memoization preserved until React Compiler is enabled across the workspace
  const activeInstallation = useMemo(() => {
    if (!installations || installations.size === 0) return null;
    if (purchaseOrder?.targetInstallationId) {
      const inst = installations.get(purchaseOrder.targetInstallationId);
      return inst?.state === "ACTIVE" ? inst : null;
    }
    for (const [, inst] of installations) {
      if (inst.state === "ACTIVE" && (inst.serviceId === "shopify" || inst.serviceId === "woocommerce")) {
        return inst;
      }
    }
    return null;
  }, [installations, purchaseOrder?.targetInstallationId]);

  const vendorLink = useMemo(() => {
    if (!purchaseOrder || !purchaseOrder.targetObjectId || !activeInstallation || !activeInstallation.domain) {
      return null;
    }
    return getPurchaseOrderTarget(purchaseOrder, activeInstallation) ?? null;
  }, [purchaseOrder, activeInstallation]);

  if (!purchaseOrder) return null;

  const isRetryHidden = getRetryDisabledReason({ batchStatus: batch?.batchStatus }) === "retrying";
  const { orderStatus = "WaitingToBeFulfilled" } = purchaseOrder;

  const statusConfig = ORDER_STATUS_CONFIG[orderStatus];
  const { lineItems } = purchaseOrder;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent testId={testId ?? setOrderDetailDialogTestId(ORDER_DETAIL_SECTIONS.content)} className="sm:max-w-[850px]">
          <DialogHeader>
            <DialogTitle>
              {t("orders.orderDetail.order", {
                id: purchaseOrder.purchaseOrderId,
              })}
            </DialogTitle>
          </DialogHeader>

          <div className="min-w-0 space-y-3 [overflow-wrap:anywhere]">
            {/* Header row — status + date */}
            <div className="flex items-center gap-2" data-testid={setOrderDetailDialogTestId(ORDER_DETAIL_SECTIONS.headerRow)}>
              <Badge className={statusConfig.className} data-testid={setOrderDetailDialogTestId(ORDER_DETAIL_SECTIONS.statusBadge)}>
                <span className={`mr-1.5 inline-block h-1.5 w-1.5 rounded-full ${statusConfig.dotColor}`} />
                {t(statusConfig.labelKey) as string}
              </Badge>
              <span className="text-sm text-muted-foreground">{t("dates.long", { date: new Date(purchaseOrder.createdAt) })}</span>
            </div>

            <OrderDetailNotice
              purchaseOrder={purchaseOrder}
              onRetry={handleRetry}
              isRetryLoading={isCreating}
              isRetryHidden={isRetryHidden}
            />

            <Separator />

            {/* Shipping Address */}
            <div className="space-y-3" data-testid={setOrderDetailDialogTestId(ORDER_DETAIL_SECTIONS.shippingAddress)}>
              <div className="flex items-center gap-2 text-sm font-semibold">
                <MapPin className="h-4 w-4" />
                {t("orders.orderDetail.shippingAddress")}
              </div>
              <div className="rounded-lg border border-border bg-muted/50 p-4 text-sm">
                {purchaseOrder.shippingAddress ? (
                  <>
                    <div className="font-medium">{purchaseOrder.shippingAddress.name}</div>
                    <div className="text-muted-foreground">{purchaseOrder.shippingAddress.address1}</div>
                    {purchaseOrder.shippingAddress.address2 && (
                      <div className="text-muted-foreground">{purchaseOrder.shippingAddress.address2}</div>
                    )}
                    <div className="text-muted-foreground">
                      {[purchaseOrder.shippingAddress.city, purchaseOrder.shippingAddress.province, purchaseOrder.shippingAddress.zip]
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                    <div className="text-muted-foreground">{purchaseOrder.shippingAddress.country}</div>
                  </>
                ) : (
                  <div className="text-muted-foreground">{t("orders.orderDetail.noShippingAddress")}</div>
                )}
              </div>
            </div>

            <Separator />

            {/* Line Items Table */}
            <div className="space-y-3" data-testid={setOrderDetailDialogTestId(ORDER_DETAIL_SECTIONS.lineItem)}>
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Package className="h-4 w-4" />
                {t("orders.orderDetail.productDetails")}
              </div>
              {lineItems.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-border">
                  {/* [overflow-wrap:normal] undoes the parent's `anywhere` wrap so
                      cells break on spaces (not mid-word) and the columns stay
                      readable; the wrapper scrolls horizontally if needed. */}
                  <table className="w-full text-sm [overflow-wrap:normal]">
                    <thead>
                      <tr className="border-b bg-muted/50 text-left text-muted-foreground">
                        <th className="whitespace-nowrap px-3 py-2 font-medium">{t("orders.orderDetail.product")}</th>
                        <th className="whitespace-nowrap px-3 py-2 font-medium">{t("orders.orderDetail.quantity")}</th>
                        <th className="whitespace-nowrap px-3 py-2 font-medium">{t("orders.orderDetail.grind")}</th>
                        <th className="whitespace-nowrap px-3 py-2 font-medium">{t("orders.orderDetail.weight")}</th>
                        <th className="whitespace-nowrap px-3 py-2 font-medium">{t("orders.orderDetail.tracking")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/50">
                      {lineItems.map((li, index) => (
                        <tr key={li.id ?? index}>
                          <td className="px-3 py-2 align-top">
                            <div className="font-medium">{li.productTitle ?? "—"}</div>
                            {li.variantTitle && <div className="text-xs text-muted-foreground">{li.variantTitle}</div>}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 align-top">{li.quantity}</td>
                          <td className="px-3 py-2 align-top">
                            {li.grindType ? (
                              <Badge variant="secondary" className="text-xs font-normal">
                                {configUtils?.findTranslatedValue("grind", li.grindType) ?? li.grindType}
                              </Badge>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 align-top">
                            {li.lineItemWeightInGrams
                              ? t("weight.weightWithSuffix", {
                                  grams: li.lineItemWeightInGrams,
                                })
                              : "—"}
                          </td>
                          <td className="px-3 py-2 align-top">
                            {li.trackingNumber ? (
                              <div className="flex items-center gap-1.5">
                                <CarrierIcon carrier={li.carrier} />
                                {li.trackingUrl ? (
                                  <a
                                    href={li.trackingUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-mono text-xs text-[var(--link)] hover:underline"
                                  >
                                    {li.trackingNumber}
                                  </a>
                                ) : (
                                  <span className="font-mono text-xs">{li.trackingNumber}</span>
                                )}
                              </div>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">{t("orders.orderDetail.noLineItems")}</div>
              )}
            </div>

            <Separator />

            {/* Order Info */}
            <div className="grid grid-cols-2 gap-4 text-sm" data-testid={setOrderDetailDialogTestId(ORDER_DETAIL_SECTIONS.metadata)}>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">{t("orders.orderDetail.marketplace")}</span>
                <span className="font-medium">{getMarketplaceDisplayName(purchaseOrder, customerAccountCarrierDetails ?? [])}</span>
              </div>
              {purchaseOrder.batchId && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">{t("orders.orderDetail.batch")}</span>
                  <button
                    type="button"
                    className={`cursor-pointer bg-transparent border-none p-0 font-mono text-sm ${CELL_LINK_CLASSES}`}
                    data-testid={setOrderDetailDialogTestId(ORDER_DETAIL_SECTIONS.batchLink)}
                    onClick={handleBatchLinkClick}
                  >
                    BATCH-{readableBatchId(purchaseOrder.batchId)}
                  </button>
                </div>
              )}
            </div>

            <Separator />

            {/* Actions */}
            <div data-testid={setOrderDetailDialogTestId(ORDER_DETAIL_SECTIONS.actions)}>
              <OrderDetailShippingButtons
                purchaseOrder={purchaseOrder}
                batch={batch}
                onOpenShippingDocuments={handleOpenShippingDocuments}
                vendorLink={vendorLink}
                vendorServiceId={activeInstallation?.serviceId}
                vendorLinkTestId={setOrderDetailDialogTestId(ORDER_DETAIL_SECTIONS.vendorLink)}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {shippingDocsOpen && (
        <Suspense fallback={null}>
          <ShippingDocumentsDialog
            open={shippingDocsOpen}
            onOpenChange={setShippingDocsOpen}
            purchaseOrders={[purchaseOrder]}
            initialTab={shippingDocsInitialTab}
          />
        </Suspense>
      )}
    </>
  );
}
